import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = () => {
  const urgencyIndicators = [
    {
      icon: 'Clock',
      text: 'Durchschnittlich 2 Min Wartezeit',
      color: 'text-conversion-accent'
    },
    {
      icon: 'Phone',
      text: '24/7 erreichbar',
      color: 'text-trust-builder'
    },
    {
      icon: 'Shield',
      text: 'Zertifizierte Anwälte',
      color: 'text-primary'
    }
  ];

  const contactMethods = [
    {
      id: 1,
      type: 'immediate',
      icon: 'Phone',
      title: 'Sofort anrufen',
      subtitle: 'Direkter Kontakt zu unseren Anwälten',
      price: '2,99 €/Min',
      description: 'Für dringende rechtliche Fragen',
      buttonText: 'Jetzt anrufen',
      buttonVariant: 'default',
      buttonClass: 'bg-conversion-accent hover:bg-cta text-white cta-button',
      urgent: true
    },
    {
      id: 2,
      type: 'scheduled',
      icon: 'Calendar',
      title: 'Termin vereinbaren',
      subtitle: 'Geplante Beratung zu Ihrem Wunschtermin',
      price: 'Ab 50 €',
      description: 'Für ausführliche Beratungsgespräche',
      buttonText: 'Termin buchen',
      buttonVariant: 'outline',
      buttonClass: 'border-white text-white hover:bg-white hover:text-primary',
      urgent: false
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-primary via-brand-primary to-accent relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-white rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Rechtliche Hilfe kann nicht warten
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Professionelle Rechtsberatung, wenn Sie sie brauchen. 
              Wählen Sie Ihren bevorzugten Weg zur rechtlichen Lösung.
            </p>

            {/* Urgency Indicators */}
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              {urgencyIndicators?.map((indicator, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2"
                >
                  <Icon name={indicator?.icon} size={18} className="text-white" />
                  <span className="text-white text-sm font-medium">
                    {indicator?.text}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Contact Methods */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {contactMethods?.map((method, index) => (
            <motion.div
              key={method?.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`relative bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all duration-300 ${
                method?.urgent ? 'ring-2 ring-conversion-accent ring-opacity-50' : ''
              }`}
            >
              {/* Urgent Badge */}
              {method?.urgent && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-conversion-accent text-white px-4 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                    <Icon name="Zap" size={12} />
                    <span>Sofortige Hilfe</span>
                  </div>
                </div>
              )}

              <div className="text-center space-y-6">
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full">
                  <Icon name={method?.icon} size={40} className="text-white" />
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <h3 className="text-2xl font-bold text-white">
                    {method?.title}
                  </h3>
                  <p className="text-white/80">
                    {method?.subtitle}
                  </p>
                  <div className="text-3xl font-bold text-white">
                    {method?.price}
                  </div>
                  <p className="text-white/70 text-sm">
                    {method?.description}
                  </p>
                </div>

                {/* Action Button */}
                <div className="pt-4">
                  {method?.type === 'immediate' ? (
                    <a href="tel:09001000312">
                      <Button
                        variant={method?.buttonVariant}
                        size="lg"
                        iconName={method?.icon}
                        iconPosition="left"
                        className={`${method?.buttonClass} w-full text-lg py-4`}
                      >
                        {method?.buttonText}
                      </Button>
                    </a>
                  ) : (
                    <Link to="/contact" className="block">
                      <Button
                        variant={method?.buttonVariant}
                        size="lg"
                        iconName={method?.icon}
                        iconPosition="left"
                        className={`${method?.buttonClass} w-full text-lg py-4`}
                      >
                        {method?.buttonText}
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center space-y-8"
        >
          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Icon name="Users" size={32} className="text-white mx-auto" />
              <div className="text-2xl font-bold text-white">Viele</div>
              <div className="text-white/80 text-sm">Zufriedene Kunden</div>
            </div>
            
            <div className="space-y-2">
              <Icon name="Award" size={32} className="text-white mx-auto" />
              <div className="text-2xl font-bold text-white">Top</div>
              <div className="text-white/80 text-sm">Google Bewertung</div>
            </div>
            
            <div className="space-y-2">
              <Icon name="Clock" size={32} className="text-white mx-auto" />
              <div className="text-2xl font-bold text-white">24/7</div>
              <div className="text-white/80 text-sm">Verfügbarkeit</div>
            </div>
          </div>

          {/* Legal Notice */}
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-6 max-w-2xl mx-auto">
            <p className="text-white/70 text-sm leading-relaxed">
              AdvoCall ist eine Marke der ADVOCALL RECHTSANWALTSGESELLSCHAFT MBH.
              Alle Beratungen werden von zugelassenen Rechtsanwälten durchgeführt.
              Preise verstehen sich zzgl. der Verbindungskosten Ihres Telefonanbieters.
            </p>
          </div>

          {/* Secondary Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/about-us">
              <Button
                variant="ghost"
                iconName="Info"
                iconPosition="left"
                className="text-white hover:bg-white/10"
              >
                Mehr über uns erfahren
              </Button>
            </Link>
            
            <Link to="/trust-center">
              <Button
                variant="ghost"
                iconName="Star"
                iconPosition="left"
                className="text-white hover:bg-white/10"
              >
                Kundenbewertungen ansehen
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;